import { importLibrary } from '@googlemaps/js-api-loader';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import * as focusTrap from 'focus-trap';

import { configureGoogleMapsLoader } from '../../googleMapsLoader';
import { getBusinessHoursLabel, requestBusinessHoursLabels, subscribeBusinessHours } from './businessHoursService';

export interface Location {
  title: string;
  lat: string;
  lng: string;
  badge: {
    class: string | null;
    content: string | null;
  };
  phone: string | null;
  address: string | null;
  url: string;
  img: string;
  key: string;
  reputationId: string | null;
}

export interface LocationMapProps {
  googlemaps: { mapId: string; apiKey: string };
  options: {
    showCardList: boolean | 'true' | 'false';
    /** Whether clicking a pin opens its popover. Defaults to true. When false, pins render
     *  but are non-interactive (no click, no popover) — e.g. a display-only Razor map. */
    enableLocationPinPopovers?: boolean | 'true' | 'false';
  };
  locations: Location[];
  /** Fired when the user activates any link or button inside a location's CARD or POPOVER
   *  (title, phone, get-directions). Receives `Location.key`. Deliberately generic — this class
   *  stays Coveo-agnostic so the Razor `<vc:location-map>` mount, which has no search engine,
   *  simply omits it. The Coveo click/UA logging lives in Search/LocationMapView.tsx.
   *
   *  Not fired for the popover's close button (dismissing isn't intent to visit) or for map pin
   *  clicks (a pin only reveals the popover; the popover's own targets report instead). */
  onLocationInteraction?: (key: string) => void;
}

const DEFAULT_CENTER = { lat: 37.764, lng: -97.002 };
const DEFAULT_ZOOM = 10;
const MARKER_OPEN_ZOOM_LEVEL = 8;
// Gap (px) kept between an open pin and the bottom edge of the visible map frame, so the
// popover (which opens upward from the pin) has maximum room above it and isn't clipped.
const PIN_GAP_FROM_BOTTOM_PX = 24;
// Fallback downward nudge (px) applied when the map height can't be read (0, e.g. mid
// layout/resize) so we can't compute the height-relative offset. Roughly a third of a
// typical map frame — enough to clear space above the pin for the popover without risking
// pushing the pin off-screen on a short map.
const FALLBACK_PIN_NUDGE_PX = 150;
const MAX_FIT_BOUNDS_ZOOM = 16;
// Reveal cap must exceed the clusterer's own maxZoom (SuperClusterAlgorithm
// default = 16, inclusive) so the cluster is guaranteed to have broken apart.
// 22 (Google Maps' max zoom in well-mapped areas) gives the step-zoom loop the
// most room to separate tightly-packed markers. The reveal can feel deep on
// geographically tight clusters, but leaving them stuck without a popover is worse.
const MAX_REVEAL_ZOOM = 22;
// Upper bound on how long the cluster-reveal loop is allowed to wait before giving up.
// Each step listens for `clusteringend`, which only fires when the map's `idle` event
// triggers a recompute. A no-op fitBounds/setZoom (already at the target view) can leave
// us waiting on an event that never arrives — without this ceiling, openMarker would
// hang on `await` and the popover would never appear.
const REVEAL_TIMEOUT_MS = 2000;
const FIT_BOUNDS_PADDING = {
  top: 100, // Account for pin height (92px) + some buffer
  bottom: 50,
  left: 50,
  right: 50,
};

// Overlay exposes a one-shot callback that fires when `onAdd` runs (i.e. the popover
// has been appended to the floatPane). Used to defer focus-trap activation until the
// trap container is actually in the DOM — `setMap()` doesn't call `onAdd` synchronously.
type PopoverOverlay = google.maps.OverlayView & {
  setOnAddCallback(cb: (() => void) | null): void;
};
type PopoverOverlayCtor = new (position: google.maps.LatLng, rootEl: HTMLElement) => PopoverOverlay;

// Flatten a server-rendered FormattedAddress into a single-line plain-text string
// suitable for URL parameters (Google Maps directions, mailto bodies, etc.).
// Replaces any "<br>" tags with a space separator and collapses whitespace.
function flattenAddressToSingleLine(address: string | null): string {
  return (address ?? '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Server-rendered FormattedAddress contains a literal "<br>" between street and city lines.
// Split on it and rebuild with real <br> elements so editor-supplied text never goes through HTML parsing.
// `<br>` is the only authoritative line break — any other whitespace (including stray newlines
// from CMS content) is collapsed so it doesn't leak into the clipboard / accessibility tree.
function appendAddressLines(parent: HTMLElement, address: string | null) {
  const parts = (address ?? '').split(/<br\s*\/?>/i);
  let appendedAny = false;
  parts.forEach((part) => {
    const normalized = part.replace(/\s+/g, ' ').trim();
    if (!normalized) return;
    if (appendedAny) {
      parent.appendChild(document.createElement('br'));
    }
    parent.appendChild(document.createTextNode(normalized));
    appendedAny = true;
  });
}

export default class LocationMap {
  // Single document-level observer that re-runs the scan whenever a subtree containing
  // a LocationMap host is inserted. Lets us catch hosts added after DOMContentLoaded —
  // notably Kentico's page-builder section AJAX re-renders in the admin.
  private static rescanObserver: MutationObserver | null = null;

  private readonly host: HTMLElement;
  private readonly props: LocationMapProps;
  private readonly showCardList: boolean;
  private readonly enablePinPopovers: boolean;

  private mounted = false;
  private destroyed = false;
  private mapElement: HTMLElement | null = null;
  private cardListElement: HTMLUListElement | null = null;
  private map: google.maps.Map | null = null;
  private markers: google.maps.marker.AdvancedMarkerElement[] = [];
  private cluster: MarkerClusterer | null = null;
  private defaultBounds: google.maps.LatLngBounds | null = null;
  private currentOpenIndex = 100;
  private AdvancedMarkerElementCtor: typeof google.maps.marker.AdvancedMarkerElement | null = null;
  // Popover (details panel) DOM and its OverlayView, keyed by the marker they belong to.
  // The popover lives in the map's floatPane — not inside the AdvancedMarkerElement —
  // because Google warns against focusable children inside an AdvancedMarkerElement.
  // Populated LAZILY — a marker's popover + overlay are built by ensurePopover() on the
  // marker's first open, not up front in renderLocations. Both maps therefore only ever
  // hold entries for markers the user has actually opened; they're cleared together in
  // clearLocations. (Deferring the build keeps ~N popover DOM trees off the initial
  // render's main thread and stops every location image from loading on page load — the
  // popover <img>'s src is only set when the popover is built.)
  private detailsByMarker: Map<google.maps.marker.AdvancedMarkerElement, HTMLElement> = new Map();
  private overlaysByMarker: Map<google.maps.marker.AdvancedMarkerElement, PopoverOverlay> = new Map();
  // Source location for each marker whose popover has NOT been built yet, kept so
  // ensurePopover() can build the popover on demand. Populated for every marker in
  // renderLocations (when popovers are enabled); ensurePopover removes an entry once it
  // builds that marker's popover (the Location is then unneeded), and clearLocations wipes
  // the rest. So this only ever holds pending (not-yet-opened) markers.
  private locationsByMarker: Map<google.maps.marker.AdvancedMarkerElement, Location> = new Map();
  // OverlayView subclass that wraps a popover element. Defined lazily on first use
  // because `google.maps.OverlayView` only exists after `importLibrary('maps')` resolves.
  private PopoverOverlayCtor: PopoverOverlayCtor | null = null;
  // Trap for the currently open info window. At most one marker is open at a time,
  // so we only need a single reference. The library handles returning focus to the
  // previously-focused element (e.g., the card title button) on deactivation.
  private activeFocusTrap: focusTrap.FocusTrap | null = null;
  // Subscription to the business-hours service; bound in mount(), released in destroy().
  private hoursUnsubscribe: (() => void) | null = null;

  private readonly onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    this.resetMap();
  };

  constructor(host: HTMLElement, props: LocationMapProps) {
    this.host = host;
    this.props = props;
    this.showCardList = props.options?.showCardList === true || props.options?.showCardList === 'true';
    // Defaults to true — only an explicit false / 'false' disables pin popovers.
    const enablePinPopovers = props.options?.enableLocationPinPopovers;
    this.enablePinPopovers = enablePinPopovers !== false && enablePinPopovers !== 'false';
  }

  /**
   * Locate every Razor-rendered host element on the page and mount a LocationMap on each.
   * Hosts that have already been mounted are skipped so the scan is safe to re-run
   * (e.g. after the Kentico page-builder re-renders a section's DOM).
   */
  static init(): LocationMap[] {
    const hosts = document.querySelectorAll<HTMLElement>('[data-hook="locationMap"]');
    const instances: LocationMap[] = [];
    hosts.forEach((host) => {
      if (host.dataset.locationMapMounted === '1') return;
      const instance = LocationMap.fromDataAttribute(host);
      // Leave the host unflagged on parse failure so a later rescan (e.g. after the
      // page builder swaps the section's DOM with valid data-props) can retry it.
      if (!instance) return;
      // Flag synchronously before mount()'s first await so a rescan triggered during
      // the async Google Maps library load can't double-mount this host.
      host.dataset.locationMapMounted = '1';
      instance.mount();
      instances.push(instance);
    });

    LocationMap.ensureRescanObserver();

    return instances;
  }

  /**
   * Registers a one-shot document observer that re-runs `init()` whenever a subtree
   * containing a LocationMap host is inserted. Required for the Kentico page builder,
   * where editing or adding a section replaces the section's HTML via AJAX long after
   * DOMContentLoaded has fired. The `data-location-map-mounted` flag set in `init()`
   * makes the re-scan idempotent — already-mounted hosts are skipped.
   */
  private static ensureRescanObserver(): void {
    if (LocationMap.rescanObserver) return;
    if (typeof MutationObserver === 'undefined' || !document.body) return;

    LocationMap.rescanObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          // The host either *is* the added node (unlikely but possible) or sits inside
          // a larger fragment Kentico just dropped in. mount()'s own DOM additions
          // (the map div and card list) are descendants of an already-mounted host and
          // never themselves match `[data-hook="locationMap"]`, so re-entrancy is bounded.
          if (node.matches('[data-hook="locationMap"]') || node.querySelector('[data-hook="locationMap"]')) {
            LocationMap.init();
            return;
          }
        }
      }
    });
    LocationMap.rescanObserver.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * Build a LocationMap from a host element whose `data-props` attribute contains the JSON config.
   * Returns null if `data-props` is missing or unparseable.
   */
  static fromDataAttribute(host: HTMLElement): LocationMap | null {
    const raw = host.dataset.props;
    if (!raw) {
      console.warn('LocationMap: data-props missing on host element.');
      return null;
    }
    try {
      const props = JSON.parse(raw) as LocationMapProps;
      return new LocationMap(host, props);
    } catch (error) {
      console.error('LocationMap: unable to parse data-props.', error);
      return null;
    }
  }

  /**
   * Loads the Google Maps API and renders the map, markers, cluster, and (optional) card list.
   */
  async mount(): Promise<void> {
    if (this.mounted) {
      console.warn('LocationMap already mounted; skipping.');
      return;
    }
    if (this.destroyed) return;

    configureGoogleMapsLoader(this.props.googlemaps.apiKey);

    const { Map } = (await importLibrary('maps')) as google.maps.MapsLibrary;
    if (this.destroyed) return;

    const { AdvancedMarkerElement } = (await importLibrary('marker')) as google.maps.MarkerLibrary;
    if (this.destroyed) return;

    this.AdvancedMarkerElementCtor = AdvancedMarkerElement;

    this.mapElement = document.createElement('div');
    this.mapElement.classList.add('location-map__map');
    this.host.appendChild(this.mapElement);

    this.map = new Map(this.mapElement, {
      mapId: this.props.googlemaps.mapId,
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      zoomControl: true,
      cameraControl: true,
      mapTypeControl: false,
      scaleControl: true,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: true,
    });

    if (this.showCardList) {
      this.cardListElement = document.createElement('ul');
      this.cardListElement.classList.add('location-map__card-list');
      this.cardListElement.setAttribute('role', 'list');
      this.host.prepend(this.cardListElement);
    }

    this.cluster = new MarkerClusterer({
      markers: [],
      map: this.map,
      renderer: {
        render: ({ count, position }: any) => {
          return new AdvancedMarkerElement({
            position,
            title: `${String(count)} locations`,
            content: LocationMap.buildClusterMarkerContent(String(count)),
          });
        },
      },
      onClusterClick: (_, cluster) => {
        if (cluster.bounds && this.map) {
          this.map.fitBounds(cluster.bounds, FIT_BOUNDS_PADDING);
        }
      },
    });

    document.addEventListener('keydown', this.onKeyDown);

    // Subscribe to the shared business-hours service. The listener walks
    // currently-rendered card-list and popover badges and patches matching
    // ones in place. Set up before renderLocations so any cache hits from
    // earlier instances flush in immediately.
    this.hoursUnsubscribe = subscribeBusinessHours(() => this.refreshHoursBadges());

    this.renderLocations(this.props.locations);
    this.mounted = true;
  }

  /**
   * Replace the rendered pins with a new set of locations. No-op if not yet mounted.
   */
  update(locations: Location[]): void {
    if (!this.mounted || !this.map) {
      console.warn('LocationMap.update called before mount.');
      return;
    }
    this.clearLocations();
    this.renderLocations(locations);
  }

  /**
   * Tear down the map, markers, cluster, listeners, and any DOM injected into the host.
   */
  destroy(): void {
    this.destroyed = true;
    document.removeEventListener('keydown', this.onKeyDown);
    this.hoursUnsubscribe?.();
    this.hoursUnsubscribe = null;
    this.clearLocations();

    if (this.cluster) {
      this.cluster.setMap(null);
      this.cluster = null;
    }

    this.cardListElement?.remove();
    this.cardListElement = null;

    this.mapElement?.remove();
    this.mapElement = null;

    this.map = null;
    this.AdvancedMarkerElementCtor = null;
    this.defaultBounds = null;
    this.mounted = false;

    delete this.host.dataset.locationMapMounted;
  }

  private clearLocations(): void {
    if (this.activeFocusTrap) {
      const trap = this.activeFocusTrap;
      this.activeFocusTrap = null;
      trap.deactivate();
    }
    this.overlaysByMarker.forEach((overlay) => {
      overlay.setMap(null);
    });
    this.overlaysByMarker.clear();
    this.detailsByMarker.clear();
    this.locationsByMarker.clear();
    this.markers.forEach((marker) => {
      marker.map = null;
    });
    this.markers = [];
    this.cluster?.clearMarkers();
    this.cardListElement?.replaceChildren();
    this.defaultBounds = null;
    this.currentOpenIndex = 100;
  }

  private renderLocations(locations: Location[]): void {
    // Hoisted to locals so the null checks below narrow INSIDE the forEach callback. Narrowing
    // `this.map` / `this.AdvancedMarkerElementCtor` doesn't survive the closure boundary — they're
    // mutable properties, so TypeScript re-widens them and the loop body previously needed
    // non-null assertions (DeepSource JS-0323). These locals are immutable, so the narrowing
    // holds throughout. `bounds` is local for the same reason and mirrored onto
    // `this.defaultBounds`, which other methods (recenter, resize) still read.
    // Use the locals — not `this.*` — everywhere in this method, or the assertions come back.
    const map = this.map;
    const MarkerCtor = this.AdvancedMarkerElementCtor;
    if (!map || !MarkerCtor) return;

    const bounds = new google.maps.LatLngBounds();
    this.defaultBounds = bounds;

    locations.forEach((location) => {
      const lat = LocationMap.parseCoord(location.lat);
      const lng = LocationMap.parseCoord(location.lng);
      if (lat === null || lng === null) return;

      const position = new google.maps.LatLng(lat, lng);
      bounds.extend(position);

      const marker = new MarkerCtor({
        position,
        map,
        title: location.title,
        content: LocationMap.buildPinContent(),
        // When popovers are disabled the pin is display-only — not clickable or focusable.
        gmpClickable: this.enablePinPopovers,
      });

      // Only wire the click when popovers are enabled. When disabled the pins render but
      // there's nothing to open, so skip the popover work entirely.
      if (this.enablePinPopovers) {
        // Defer the popover DOM + OverlayView build to the marker's first open
        // (openMarker → ensurePopover). Building every location's popover up front — each
        // with an <img> whose src loads immediately — was a large chunk of the initial
        // render's main-thread work and eagerly fetched every location image on page load.
        // Stash the source location so the popover can be built on demand.
        this.locationsByMarker.set(marker, location);

        marker.addEventListener('gmp-click', () => {
          // Clicking the marker only opens the info window. Closing is handled by the
          // dedicated close button inside the popover (and by the Escape key).
          this.openMarker(marker, position);
        });
      }

      this.markers.push(marker);

      if (this.cardListElement) {
        this.cardListElement.append(this.buildCardListItem(location, marker, position));
      }
    });

    this.cluster?.addMarkers(this.markers);

    // Kick the business-hours fetch for any locations that have a
    // reputation_id. The service dedupes against its cache, so re-renders
    // (Coveo update(), Razor remount) won't re-POST already-known ids. The
    // synchronous refresh after the request catches any badges whose ids
    // were already cached from a prior instance — they skip the loading
    // state entirely.
    const reputationIds = locations.map((l) => l.reputationId).filter((id): id is string => Boolean(id));
    if (reputationIds.length > 0) {
      requestBusinessHoursLabels(reputationIds);
      this.refreshHoursBadges();
    }

    if (this.markers.length > 0 && !bounds.isEmpty()) {
      this.fitBoundsWithMaxZoom(bounds);
    } else {
      // Empty result set — fall back to default view.
      map.setCenter(DEFAULT_CENTER);
      map.setZoom(DEFAULT_ZOOM);
    }
  }

  /**
   * Patch any rendered business-hours badges whose reputation_id has a
   * cached label. Walks both the card list (in this.host) and the popover
   * details elements that have been built so far (held in detailsByMarker —
   * popovers are built lazily on first open, and a not-yet-opened marker has
   * no popover to patch; ensurePopover applies the cached label when it later
   * builds one). Idempotent and safe to call repeatedly.
   */
  private refreshHoursBadges(): void {
    this.host.querySelectorAll<HTMLElement>('[data-hours-badge]').forEach((badge) => {
      LocationMap.updateHoursBadge(badge);
    });
    this.detailsByMarker.forEach((details) => {
      details.querySelectorAll<HTMLElement>('[data-hours-badge]').forEach((badge) => {
        LocationMap.updateHoursBadge(badge);
      });
    });
  }

  private static updateHoursBadge(badge: HTMLElement): void {
    const id = badge.dataset.reputationId;
    if (!id) return;
    const label = getBusinessHoursLabel(id);
    if (label === undefined) return; // still pending -- leave the loading pulse
    if (label === null) {
      // Resolved with no label -- hide the badge rather than pulse forever.
      badge.remove();
      return;
    }
    badge.textContent = label;
    badge.classList.remove('badge--loading');
  }

  /**
   * Calls fitBounds, then caps the resulting zoom — needed because a single-pin or
   * tightly-clustered bounds otherwise zooms past street level.
   */
  private fitBoundsWithMaxZoom(bounds: google.maps.LatLngBounds): void {
    if (!this.map) return;
    this.map.fitBounds(bounds, FIT_BOUNDS_PADDING);
    google.maps.event.addListenerOnce(this.map, 'idle', () => {
      const zoom = this.map?.getZoom();
      if (zoom !== undefined && zoom > MAX_FIT_BOUNDS_ZOOM) {
        this.map?.setZoom(MAX_FIT_BOUNDS_ZOOM);
      }
    });
  }

  private static parseCoord(val: string): number | null {
    const n = Number(val);
    if (Number.isNaN(n)) {
      console.warn('Invalid coordinate:', val);
      return null;
    }
    return n;
  }

  private resetMap(): void {
    if (!this.map || !this.defaultBounds) return;
    this.closeAllMarkers();
    this.fitBoundsWithMaxZoom(this.defaultBounds);
  }

  private static buildClusterMarkerContent(count: string): HTMLElement {
    const content = document.createElement('div');
    content.classList.add('location-info-window');

    const pin = document.createElement('div');
    pin.classList.add('location-info-window__pin', 'location-info-window__pin--cluster');

    const countSpan = document.createElement('span');
    countSpan.textContent = count;
    pin.appendChild(countSpan);

    content.appendChild(pin);
    return content;
  }

  /**
   * Build the content placed inside the AdvancedMarkerElement — just the pin.
   * Per Google's guidance, AdvancedMarkerElement should not contain focusable children;
   * the popover with its buttons/links lives in a separate OverlayView.
   */
  private static buildPinContent(): HTMLElement {
    const content = document.createElement('div');
    content.classList.add('location-info-window');

    const pin = document.createElement('div');
    pin.classList.add('location-info-window__pin', 'location-info-window__pin--single');

    content.appendChild(pin);
    return content;
  }

  /**
   * Returns the marker's popover DOM + OverlayView, building them on first call and
   * caching for subsequent opens. This is the lazy-build entry point: renderLocations no
   * longer constructs popovers up front (see the note on `detailsByMarker`), so the
   * popover — including its <img> — is only created the first time a marker is opened.
   *
   * Returns null only if the marker has no stashed location (shouldn't happen for a
   * marker wired via renderLocations); callers guard on the result.
   */
  private ensurePopover(
    marker: google.maps.marker.AdvancedMarkerElement,
    position: google.maps.LatLng,
  ): { details: HTMLElement; overlay: PopoverOverlay } | null {
    const existingDetails = this.detailsByMarker.get(marker);
    const existingOverlay = this.overlaysByMarker.get(marker);
    if (existingDetails && existingOverlay) {
      return { details: existingDetails, overlay: existingOverlay };
    }

    const location = this.locationsByMarker.get(marker);
    if (!location) return null;

    // Build the popover after the marker exists so its close button can hold a direct
    // reference to this marker. The popover is wrapped in an OverlayView and only added
    // to the map when the marker is opened (by the caller).
    const details = this.buildPopoverContent(location, marker);
    const PopoverOverlay = this.getPopoverOverlayClass();
    const overlay = new PopoverOverlay(position, details);
    this.detailsByMarker.set(marker, details);
    this.overlaysByMarker.set(marker, overlay);
    // The Location has served its only purpose (building the popover); drop it so it isn't
    // retained for the map's lifetime. Safe because future ensurePopover calls short-circuit
    // on the cached detailsByMarker/overlaysByMarker entries above and never re-read this.
    // Deleting after the sets means a throw in buildPopoverContent leaves the entry for retry.
    this.locationsByMarker.delete(marker);

    // Apply any already-resolved business-hours label immediately so a popover built
    // after its label arrived doesn't flash the `***` loading placeholder. Ongoing
    // updates while it's open are handled by refreshHoursBadges (the popover is now in
    // detailsByMarker). Uses the same per-badge patch as refreshHoursBadges.
    details.querySelectorAll<HTMLElement>('[data-hours-badge]').forEach((badge) => {
      LocationMap.updateHoursBadge(badge);
    });

    return { details, overlay };
  }

  /**
   * Build the popover (details panel) DOM. Rendered inside an OverlayView, so it lives
   * in the map's floatPane rather than inside the marker. The close-button click handler
   * holds a direct reference to the marker, so no DOM traversal is needed at click time.
   */
  private buildPopoverContent(location: Location, marker: google.maps.marker.AdvancedMarkerElement): HTMLElement {
    const id = `locationInfoWindow_${location.key}`;

    const details = document.createElement('div');
    details.classList.add('location-info-window__details');
    details.setAttribute('id', `${id}_details`);
    details.style.fontFamily = "Figtree, 'Helvetica Neue', 'Arial Nova', 'Nimbus Sans', system-ui, sans-serif";

    const closeButton = document.createElement('button');
    closeButton.setAttribute('type', 'button');
    closeButton.classList.add('location-info-window__close');
    const closeLabel = document.createElement('span');
    closeLabel.classList.add('visually-hidden');
    closeLabel.textContent = 'Close';
    closeButton.appendChild(closeLabel);
    closeButton.addEventListener('click', () => {
      this.closeMarker(marker);
    });

    const img = document.createElement('img');
    img.setAttribute('src', location.img);
    img.setAttribute('aria-hidden', 'true');
    img.classList.add('location-info-window__image');

    const innerWrapper = document.createElement('div');

    // Skip the badge entirely when the location has no reputation_id —
    // there's no key to look up hours for. Cached labels arrive via the
    // service subscription; refreshHoursBadges patches matching badges in
    // place using the data-* attributes set below.
    let badge: HTMLSpanElement | null = null;
    if (location.reputationId) {
      badge = document.createElement('span');
      badge.classList.add('badge', 'mb-2', 'badge-style-8', 'badge--loading');
      badge.textContent = '***';
      badge.dataset.hoursBadge = '';
      badge.dataset.reputationId = location.reputationId;
    }

    const link = document.createElement('a');
    link.setAttribute('href', location.url);
    link.textContent = location.title;
    link.classList.add('location-info-window__title');

    const addressP = document.createElement('p');
    appendAddressLines(addressP, location.address);
    addressP.classList.add('location-info-window__address');

    if (badge) innerWrapper.appendChild(badge);
    innerWrapper.appendChild(link);
    innerWrapper.appendChild(addressP);

    const footer = LocationMap.buildContactFooter(location, 'location-info-window__footer');
    if (footer) innerWrapper.appendChild(footer);

    details.appendChild(closeButton);
    details.appendChild(img);
    details.appendChild(innerWrapper);

    details.addEventListener('click', (event) => this.notifyLocationInteraction(location, event.target));

    return details;
  }

  /**
   * Lazily defines and returns the OverlayView subclass used to position popovers.
   * Can't be defined at module load time because `google.maps.OverlayView` only exists
   * after the 'maps' library has been imported (i.e., after `mount()` runs).
   */
  private getPopoverOverlayClass(): PopoverOverlayCtor {
    if (this.PopoverOverlayCtor) return this.PopoverOverlayCtor;

    class PopoverOverlay extends google.maps.OverlayView {
      private readonly rootEl: HTMLElement;
      private readonly position: google.maps.LatLng;
      private onAddCallback: (() => void) | null = null;

      constructor(position: google.maps.LatLng, rootEl: HTMLElement) {
        super();
        this.position = position;
        this.rootEl = rootEl;
        this.rootEl.style.position = 'absolute';
        // The popover lives in the map's floatPane (inside the map container), where Google
        // Maps' own contextmenu handler preventDefault()s right-clicks -- which otherwise
        // swallows the browser's native menu on the phone/directions links. Stop the event
        // here so it never reaches that ancestor handler; the native menu (Open in new tab /
        // Copy link / Call) then works. Added once on the reused rootEl (one popover per marker).
        this.rootEl.addEventListener('contextmenu', (event) => event.stopPropagation());
      }

      setOnAddCallback(cb: (() => void) | null) {
        this.onAddCallback = cb;
      }

      onAdd() {
        const panes = this.getPanes();
        panes?.floatPane.appendChild(this.rootEl);
        // Fire-once handoff: callers that need the popover to actually be in the DOM
        // (focus-trap activation) hook here. Clear the slot so a stale callback can't
        // fire on a subsequent open.
        const cb = this.onAddCallback;
        this.onAddCallback = null;
        cb?.();
      }

      draw() {
        const projection = this.getProjection();
        if (!projection) return;
        const pixel = projection.fromLatLngToDivPixel(this.position);
        if (!pixel) return;
        this.rootEl.style.left = `${pixel.x}px`;
        this.rootEl.style.top = `${pixel.y}px`;
      }

      onRemove() {
        this.rootEl.parentNode?.removeChild(this.rootEl);
      }
    }

    this.PopoverOverlayCtor = PopoverOverlay;
    return this.PopoverOverlayCtor;
  }

  /**
   * Builds a footer with a phone link and a "Get directions" link for the given location.
   * Returns null when the location has neither a phone number nor a usable address.
   */
  private static buildContactFooter(location: Location, className: string): HTMLDivElement | null {
    const footer = document.createElement('div');
    footer.classList.add(className);

    let appendedAny = false;

    if (location.phone) {
      const phoneSanitized = location.phone.replace(/[^\d+]/g, '');
      const phoneLink = document.createElement('a');
      phoneLink.setAttribute('href', `tel:${phoneSanitized}`);
      const callPrefix = document.createElement('span');
      callPrefix.classList.add('visually-hidden');
      callPrefix.textContent = 'Call ';
      phoneLink.appendChild(callPrefix);
      phoneLink.appendChild(document.createTextNode(location.phone));
      footer.appendChild(phoneLink);
      appendedAny = true;
    }

    const directionsDestination = flattenAddressToSingleLine(location.address);
    if (directionsDestination) {
      const directionsLink = document.createElement('a');
      directionsLink.setAttribute('href', `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(directionsDestination)}`);
      directionsLink.textContent = 'Get directions';
      footer.appendChild(directionsLink);
      appendedAny = true;
    }

    return appendedAny ? footer : null;
  }

  /**
   * Report a user activation inside a card or popover to `onLocationInteraction`.
   * Delegated from the card <li> / popover root so every current and future link or button
   * inside is covered without wiring each one individually.
   */
  private notifyLocationInteraction(location: Location, target: EventTarget | null): void {
    const activated = target instanceof Element ? target.closest('a, button') : null;
    // Clicks on padding, text or the image aren't an interaction with a target.
    if (!activated) return;
    // Closing the popover is the opposite of intent to visit.
    if (activated.classList.contains('location-info-window__close')) return;
    this.props.onLocationInteraction?.(location.key);
  }

  private buildCardListItem(location: Location, marker: google.maps.marker.AdvancedMarkerElement, position: google.maps.LatLng): HTMLLIElement {
    const li = document.createElement('li');
    // const button = document.createElement('button');
    // button.setAttribute('type', 'button');

    // See buildPopoverContent — same reputation_id-gated emit. Patched in
    // place when the business-hours service notifies.
    let badge: HTMLSpanElement | null = null;
    if (location.reputationId) {
      badge = document.createElement('span');
      badge.classList.add('badge', 'badge-style-8', 'badge--loading');
      badge.textContent = '***';
      badge.dataset.hoursBadge = '';
      badge.dataset.reputationId = location.reputationId;
    }

    let title: HTMLButtonElement | null = null;
    if (location.title) {
      title = document.createElement('button');
      title.setAttribute('type', 'button');
      title.classList.add('location-map__card-title');
      title.textContent = `${location.title}`;
    }

    let address: HTMLParagraphElement | null = null;
    if (location.address) {
      address = document.createElement('p');
      address.classList.add('location-map__card-address');
      appendAddressLines(address, location.address);
    }

    const footer = LocationMap.buildContactFooter(location, 'location-map__card-footer');

    if (badge) li.appendChild(badge);
    if (title) li.appendChild(title);
    if (address) li.appendChild(address);
    if (footer) li.appendChild(footer);

    title?.addEventListener('click', (event) => {
      // event.detail === 0 means the click was keyboard-activated (Enter/Space), where
      // moving focus into the popover is the desired a11y behaviour. Real mouse/touch
      // clicks (detail >= 1) skip the focus trap so the surrounding form inputs stay
      // directly clickable without a trap-dismissing first click.
      const willOpen = !marker.classList.contains('is-active');
      this.toggleMarkerState(marker, position, { trapFocus: event.detail === 0 });
      // On small screens the card list sits below the map, so a tapped card can open its
      // pin while the map is scrolled out of view, leaving no visible sign anything
      // happened. Bring the map back into the viewport when opening.
      if (willOpen) {
        this.scrollMapIntoView();
      }
    });

    li.addEventListener('click', (event) => this.notifyLocationInteraction(location, event.target));

    // li.appendChild(button);
    return li;
  }

  /**
   * Scrolls the map into view when its top is hidden behind the sticky site header (or
   * above the viewport entirely) — the small-screen case where the card list sits below
   * the map. The header height is measured at call time because it's dynamic (it shrinks
   * on scroll and varies by breakpoint), and a buffer is added so the map clears it.
   * No-op when the map's top already sits below the header, so the desktop side-by-side
   * layout (map always visible) isn't disturbed.
   */
  private scrollMapIntoView(): void {
    const mapEl = this.mapElement;
    if (!mapEl) return;

    const header = document.querySelector<HTMLElement>('.app-header');
    const SCROLL_BUFFER = 16;
    const offset = (header?.getBoundingClientRect().height ?? 0) + SCROLL_BUFFER;
    const mapTop = mapEl.getBoundingClientRect().top;
    if (mapTop >= offset) return;

    window.scrollTo({ top: Math.max(0, window.scrollY + mapTop - offset), behavior: 'smooth' });
  }

  /**
   * Whether the marker is currently folded into a multi-marker cluster. The clusterer
   * unsets the map of markers it clusters and restores it for markers rendered
   * individually, so a null map means "currently hidden inside a cluster". This reads
   * the marker's own live state, which (unlike a separately-maintained index) stays
   * accurate across the clusterer's no-op render passes.
   */
  private static isMarkerClustered(marker: google.maps.marker.AdvancedMarkerElement): boolean {
    return marker.map == null;
  }

  /**
   * Zooms in toward the marker one level at a time until the clusterer renders it
   * individually (or we hit MAX_REVEAL_ZOOM). Each step waits for the clusterer's
   * `clusteringend` before re-checking, and pans to the marker so zooming separates it
   * from its cluster neighbours rather than diving into the cluster's centroid.
   */
  private revealMarker(marker: google.maps.marker.AdvancedMarkerElement, position: google.maps.LatLng): Promise<void> {
    return new Promise((resolve) => {
      if (!this.map || !this.cluster) {
        resolve();
        return;
      }

      // Single resolution path so the timeout and the cluster-event chain can't both
      // resolve. Any clusteringend listeners still queued when we finish fire later, but
      // the `finished` guard makes stepZoom a no-op, preventing a stray setZoom after the
      // popover is already open.
      let finished = false;
      // Holder for the safety-net timer's id. Wrapped in a const object because `finish`
      // (which clears the timer) is defined before `setTimeout` returns the id.
      const timer: { id: ReturnType<typeof setTimeout> | null } = { id: null };
      const finish = () => {
        if (finished) return;
        finished = true;
        if (timer.id !== null) clearTimeout(timer.id);
        resolve();
      };
      timer.id = setTimeout(finish, REVEAL_TIMEOUT_MS);

      const stepZoom = () => {
        if (finished) return;
        if (!this.map || !this.cluster) {
          finish();
          return;
        }
        // The clusterer has rendered the marker individually — its pin (and the popover
        // anchored to it) are now at the marker's own coordinates. Done.
        if (!LocationMap.isMarkerClustered(marker)) {
          finish();
          return;
        }
        const currentZoom = this.map.getZoom() ?? 0;
        if (currentZoom >= MAX_REVEAL_ZOOM) {
          // Hit the cap with the marker still clustered — only possible when markers share
          // (or nearly share) a coordinate. No further zoom will separate them, so give up
          // and let openMarker proceed; the popover appears over the cluster pin.
          finish();
          return;
        }
        google.maps.event.addListenerOnce(this.cluster, 'clusteringend', stepZoom);
        this.map.setZoom(currentZoom + 1);
        this.map.panTo(position);
      };

      stepZoom();
    });
  }

  private async openMarker(marker: google.maps.marker.AdvancedMarkerElement, position: google.maps.LatLng, options?: { trapFocus?: boolean }): Promise<void> {
    if (!this.map) return;

    // If this marker is currently hidden inside a cluster, zoom in until the clusterer
    // renders the individual pin before we activate it — otherwise the popover would
    // attach to the cluster pin instead of the referenced location.
    if (LocationMap.isMarkerClustered(marker)) {
      await this.revealMarker(marker, position);
      if (!this.map || this.destroyed) return;
    }

    this.closeAllMarkers();
    this.currentOpenIndex++;

    marker.classList.add('is-active');
    marker.zIndex = this.currentOpenIndex;

    const pin = marker.querySelector('.location-info-window__pin');
    pin?.classList.add('is-active');

    // The popover lives in the map's floatPane (not inside the marker). It's built lazily
    // on first open (ensurePopover), then cached for subsequent opens. Attaching it to the
    // map eventually triggers OverlayView.onAdd → appends the DOM, then draw() positions it
    // at the marker's pixel coordinates.
    //
    // ensurePopover returns null only when the marker has no stashed location. There's
    // nothing to show, so revert the active state we just set (leaving an "active" pin with
    // no popover or close button would be a dead end) and bail.
    //
    // This is EXPECTED in display-only mode (enableLocationPinPopovers=false — a valid
    // config, see LocationMapViewComponent), where the card list can still wire a title
    // click to openMarker even though no popover was ever created. So only warn when
    // popovers are supposed to exist (this.enablePinPopovers) — a genuinely unexpected null
    // — otherwise an intended display-only map would spam the console on every card click.
    const popover = this.ensurePopover(marker, position);
    if (!popover) {
      if (this.enablePinPopovers) {
        console.warn('LocationMap: no popover for marker; ignoring open.');
      }
      this.closeMarker(marker);
      return;
    }
    const { details, overlay } = popover;
    overlay.setMap(this.map);
    details.setAttribute('aria-hidden', 'false');

    // Trap focus inside the popover when opened from the card list. The previously
    // focused element (the card title button) is captured by focus-trap at activation
    // time and restored when the trap is deactivated (close button click, escape, or
    // another marker opening).
    if (options?.trapFocus) {
      const activateTrap = () => {
        // Re-check state at the moment the trap is actually being activated — between
        // queuing this callback and onAdd firing, the user could have triggered another
        // open/close which would invalidate the captured marker.
        if (this.destroyed) return;
        if (!marker.classList.contains('is-active')) return;
        const closeButton = details.querySelector<HTMLElement>('.location-info-window__close');
        this.activeFocusTrap = focusTrap.createFocusTrap(details, {
          initialFocus: closeButton ?? undefined,
          // A click anywhere outside the popover dismisses the trap and is allowed to
          // proceed (focus-trap leaves focus on the clicked element when it's focusable).
          // So clicking the keyword/address inputs above the map frees focus and lands in
          // the input in a single click, instead of the trap holding focus captive. The
          // popover itself stays open until explicitly closed (close button / Escape /
          // another marker opening).
          clickOutsideDeactivates: true,
          // Escape is handled by the document-level keydown listener (resetMap), which calls
          // closeMarker → which deactivates this trap. Disabling escape here avoids two
          // independent deactivation paths racing.
          escapeDeactivates: false,
          // The map's own panTo/panBy already brings the marker (and the popover anchored to
          // it) into view. Without this, focus()'s default scroll-into-view would yank the
          // window when a card below the map is clicked.
          preventScroll: true,
          // focus-trap self-deactivates on the outside click above; clear our reference so
          // a later closeMarker() doesn't try to deactivate an already-dead trap.
          onDeactivate: () => {
            this.activeFocusTrap = null;
          },
        });
        this.activeFocusTrap.activate();
      };
      // `setMap` doesn't synchronously call onAdd — the API schedules it for the next
      // render tick. Activating the trap immediately would call .focus() on a detached
      // close button (a no-op). If the popover is already attached (e.g., on subsequent
      // open after the OverlayView has been cached), activate right away.
      if (details.isConnected) {
        activateTrap();
      } else {
        overlay.setOnAddCallback(activateTrap);
      }
    }

    const currentZoom = this.map.getZoom();
    if (currentZoom !== undefined && currentZoom < MARKER_OPEN_ZOOM_LEVEL) {
      this.map.setZoom(MARKER_OPEN_ZOOM_LEVEL);
    }
    this.map.panTo(position);

    // Nudge the map so the pin sits ~PIN_GAP_FROM_BOTTOM_PX above the bottom edge of the visible
    // map frame -- maximizing the room above the pin for the upward-opening popover so it isn't
    // clipped. panTo leaves the pin at the vertical center (mapHeight / 2 from the top); we want it
    // lower, at (mapHeight - PIN_GAP_FROM_BOTTOM_PX) from the top -- i.e. moved DOWN by
    // `verticalNudge = mapHeight / 2 - PIN_GAP_FROM_BOTTOM_PX`.
    //
    // Sign note: in panBy(x, y) a positive y shifts the map centre south, which makes a fixed pin
    // appear to move UP the screen. To move the pin DOWN we therefore pass a NEGATIVE y, hence
    // `panBy(0, -verticalNudge)` (verticalNudge is a positive magnitude). Falls back to a fixed
    // nudge when the map height isn't readable.
    //
    // Clamp to >= 0: if the container is momentarily very short (mapHeight/2 < the gap, e.g. mid
    // layout/resize) the raw magnitude goes negative; negating it would then pass a positive y and
    // flip the pan direction (pin moves up, off-screen). Clamping to 0 just leaves it centered until
    // a real height is available.
    const mapHeight = this.mapElement?.clientHeight ?? 0;
    const verticalNudge = mapHeight > 0 ? Math.max(0, mapHeight / 2 - PIN_GAP_FROM_BOTTOM_PX) : FALLBACK_PIN_NUDGE_PX;
    this.map.panBy(0, -verticalNudge);
  }

  private closeMarker(marker: google.maps.marker.AdvancedMarkerElement): void {
    // Early-return when already closed so iterating callers (closeAllMarkers) and the
    // close-button click handler can't double-deactivate the focus trap.
    if (!marker.classList.contains('is-active')) return;

    marker.classList.remove('is-active');
    marker.zIndex = 1;

    const pin = marker.querySelector('.location-info-window__pin');
    pin?.classList.remove('is-active');

    // Deactivate the trap before tearing down the popover DOM. focus-trap captures the
    // previously focused element at activation; deactivation restores focus to it —
    // for the card-list flow, that's the card's title button.
    if (this.activeFocusTrap) {
      const trap = this.activeFocusTrap;
      this.activeFocusTrap = null;
      trap.deactivate();
    }

    const details = this.detailsByMarker.get(marker);
    details?.setAttribute('aria-hidden', 'true');
    // setMap(null) triggers OverlayView.onRemove, which detaches the popover from the
    // map's floatPane.
    this.overlaysByMarker.get(marker)?.setMap(null);
  }

  private closeAllMarkers(): void {
    this.currentOpenIndex = 100;
    this.markers.forEach((marker) => {
      this.closeMarker(marker);
    });
  }

  private toggleMarkerState(marker: google.maps.marker.AdvancedMarkerElement, position: google.maps.LatLng, options?: { trapFocus?: boolean }): void {
    if (marker.classList.contains('is-active')) {
      this.closeMarker(marker);
      return;
    }
    // Fire-and-forget: openMarker is async because it may await a cluster reveal.
    this.openMarker(marker, position, options);
  }
}